import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { authApi } from '../../../services/auth/authApi';
import { coOrdinatorApi } from '../../../services/CoOrdinatorApi/coOrdinatorApi';
import { userProfileApi } from '../../../services/userProfileApi/userProfileApi';

export type StudentDetails = {
  name: string;
  emailId: string;
  college: string;
  isProfileCompleted: boolean;
  yearOfPassing: string;
  mobile: string;
  interestedCompanies: string[];
  id: string;
  currentYearOfStudy: number;
  yearOfCommencement: number;
  department: string;
  qualification: string;
  gender?: number;
  collegeRollNo?: string;
  skipMobileNumValidation: boolean;
  academicDetails: {
    tenthPercentage?: number;
    bTechSemsPercentage?: Record<string, number>;
    interPercentage?: number;
  };
  profilePic: {
    fileName: string;
    folderPath: string;
    timeStamp: string;
    bucketName: string;
    region: string;
  };
};

type AuthState = {
  email: string;
  mobile: string;
  emailError: string;
  mobileNumberError: string;
  isLoggedIn: boolean;
  isEmailAndMobileAvailable: boolean;
  studentDetails: StudentDetails;
  studentDetailsById: StudentDetails;
  yearOfPassingError: boolean;
  userId: string;
};

const initialState: AuthState = {
  email: '',
  mobile: '',
  emailError: '',
  mobileNumberError: '',
  isLoggedIn: false,
  isEmailAndMobileAvailable: false,
  yearOfPassingError: false,
  userId: '',
  studentDetailsById: {} as StudentDetails,
  studentDetails: {
    id: '',
    name: '',
    emailId: '',
    college: '',
    isProfileCompleted: false,
    yearOfPassing: '',
    mobile: '',
    interestedCompanies: [],
    currentYearOfStudy: 0,
    department: '',
    qualification: '',
    yearOfCommencement: 0,
    skipMobileNumValidation: false,
    profilePic: {
      fileName: '',
      folderPath: '',
      timeStamp: '',
      bucketName: '',
      region: ''
    },
    academicDetails: {
      bTechSemsPercentage: {}
    }
  }
};
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsYearOfPassingError: (state, action) => {
      state.yearOfPassingError = action.payload;
    },
    setMobile: (state, action) => {
      state.mobile = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    setEmailError: (state, action) => {
      state.emailError = action.payload;
    },
    setMobileNumberError: (state, action) => {
      state.mobileNumberError = action.payload;
    },
    setIsLoggedIn: (state, action) => {
      state.isLoggedIn = action.payload;
    },
    setIsEmailMobileAvailable: (state, action) => {
      state.isEmailAndMobileAvailable = action.payload;
    },
    setUpdateStudentDetails: (state, action) => {
      const { key, value } = action.payload;
      state.studentDetails = { ...state.studentDetails, [key]: value };
    },
    setUpdateStudentState: (state, action) => {
      state.studentDetails = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setResetStudentDetails: state => {
      state.studentDetails = {
        id: '',
        name: '',
        emailId: '',
        college: '',
        isProfileCompleted: false,
        yearOfPassing: '',
        mobile: '',
        currentYearOfStudy: 0,
        department: '',
        qualification: '',
        yearOfCommencement: 0,
        interestedCompanies: [],
        profilePic: {
          fileName: '',
          folderPath: '',
          timeStamp: '',
          bucketName: '',
          region: ''
        },
        academicDetails: {},
        skipMobileNumValidation: false
      };
    },
    resetAuthState: () => initialState
  },

  extraReducers: builder => {
    builder.addMatcher(
      authApi.endpoints.isEmailMobileAvailable.matchFulfilled,
      (state, action: PayloadAction<any>) => {
        state.isEmailAndMobileAvailable = action.payload.data;
      }
    );
    builder.addMatcher(
      authApi.endpoints.isEmailMobileAvailable.matchRejected,
      (state, action: PayloadAction<any>) => {
        // toast.error('Phone Number or Email Already Exists');
        state.isEmailAndMobileAvailable =
          action.payload.data.isemailmobileexists;
      }
    );
    builder.addMatcher(
      userProfileApi.endpoints.fetchUserDetails.matchFulfilled,
      (state, action) => {
        state.studentDetails = action.payload.data;
      }
    );
    builder.addMatcher(
      coOrdinatorApi.endpoints.fetchStudentProfile.matchFulfilled,
      (state, action) => {
        state.studentDetailsById = action.payload.data;
      }
    );
  }
});

export const {
  setMobile,
  setEmailError,
  setMobileNumberError,
  setIsLoggedIn,
  resetAuthState,
  setIsEmailMobileAvailable,
  setEmail,
  setUpdateStudentDetails,
  setResetStudentDetails,
  setIsYearOfPassingError,
  setUserId,
  setUpdateStudentState
} = authSlice.actions;

export default authSlice.reducer;
